import { z } from "zod";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import type { DtoolsSiPaymentTerm } from "./dtoolsSiJsonImport";
import { PPT_PAYMENT_TRANCHE_LABELS } from "./pptPaymentTrancheLabels";
import {
  buildDtoolsReviewProposalTotals,
  buildDtoolsReviewSystemViews,
  resolveSystemDisplayLabel,
  type DtoolsReviewBuildContext,
  type DtoolsReviewProposalTotals,
} from "./dtoolsReviewNavigation";
import { filterVisibleDtoolsSystems } from "./dtoolsOptionVisibility";
import { normalizeStoredDtoolsVisibilityKeys } from "./dtoolsProposalContent";

export type DtoolsPortalPaymentTerm = {
  stage: string;
  percentage: number;
  fixedAmount?: number | null;
};

export type DtoolsDisplaySettings = {
  hideItemPricing?: boolean;
  alternativeOptions?: boolean;
  /** When false, payment terms are omitted from the customer proposal until staff enable them. */
  showPaymentTerms?: boolean;
  /** System content keys hidden from customer-facing proposal output. */
  hiddenOptionKeys?: string[];
  /**
   * Stable location IDs (preferred) or room keys hidden from customer-facing proposal output.
   * Hidden rooms contribute $0 even when their parent system remains visible.
   */
  hiddenLocationKeys?: string[];
  /** When true, the Clarity tab / introduction is hidden from customer-facing output. */
  hideClarityIntroduction?: boolean;
  /**
   * When true, customers may approve individual systems from System Summary (and system tabs).
   * Default false: only approve the whole proposal.
   */
  allowIndividualSystemApproval?: boolean;
  /** Internal import placeholder systems staff explicitly chose to show customers (e.g. Unassigned). */
  customerExposeInternalOptionKeys?: string[];
  /**
   * When false, the proposal-level Executive Overview is omitted from System Summary / PDF.
   * Missing / undefined means included (backward compatible default).
   */
  includeExecutiveOverview?: boolean;
};

export const dtoolsDisplaySettingsInputSchema = z.object({
  hideItemPricing: z.boolean().optional(),
  alternativeOptions: z.boolean().optional(),
  showPaymentTerms: z.boolean().optional(),
  hideClarityIntroduction: z.boolean().optional(),
  allowIndividualSystemApproval: z.boolean().optional(),
  includeExecutiveOverview: z.boolean().optional(),
  hiddenOptionKeys: z.array(z.string()).optional(),
  hiddenLocationKeys: z.array(z.string()).optional(),
  customerExposeInternalOptionKeys: z.array(z.string()).optional(),
});

/** Missing flag defaults to included for backward compatibility. */
export function shouldIncludeExecutiveOverview(displaySettings?: DtoolsDisplaySettings | null): boolean {
  return displaySettings?.includeExecutiveOverview !== false;
}

export const DTOOLS_DEFAULT_PAYMENT_TERMS: DtoolsPortalPaymentTerm[] = [
  { stage: PPT_PAYMENT_TRANCHE_LABELS[0], percentage: 50 },
  { stage: PPT_PAYMENT_TRANCHE_LABELS[1], percentage: 40 },
  { stage: PPT_PAYMENT_TRANCHE_LABELS[2], percentage: 10 },
];

/** Customer notice when alternative options mode is enabled. */
export const DTOOLS_ALTERNATIVE_OPTIONS_NOTICE =
  "Options shown are alternatives and are not additive.";

/** Stable empty object for display-settings fallbacks (avoids new `{}` each render). */
export const EMPTY_DTOOLS_DISPLAY_SETTINGS: DtoolsDisplaySettings = {};

export function mapDtoolsPaymentTermsForPortal(terms: DtoolsSiPaymentTerm[] | undefined | null): DtoolsPortalPaymentTerm[] {
  return (terms ?? [])
    .map((term) => ({
      stage: String(term.payment ?? "").trim() || "Payment",
      percentage: Number(term.billingPercentage ?? 0),
      fixedAmount:
        term.useFixedAmount && term.billingAmount != null && Number(term.billingAmount) > 0
          ? Number(term.billingAmount)
          : null,
    }))
    .filter((t) => (t.fixedAmount != null && t.fixedAmount > 0) || (Number.isFinite(t.percentage) && t.percentage > 0));
}

export type DtoolsProposalSummaryRow = {
  key: string;
  label: string;
  amount: string;
  strong?: boolean;
  /** Largest visual weight — used for Final Price on negotiated proposals. */
  emphasis?: boolean;
  indent?: boolean;
  muted?: boolean;
  dividerBefore?: boolean;
  helperText?: string;
};

/** Hide Subtotal when it matches Proposal total and no tax/discount/adjustment layer exists. */
export function shouldShowProposalSubtotalRow(
  totals: Pick<DtoolsReviewProposalTotals, "subtotal" | "finalTotal" | "adjustmentsTotal" | "tax">,
): boolean {
  if (totals.adjustmentsTotal !== 0) return true;
  if (totals.tax != null && totals.tax > 0) return true;
  return Math.abs(totals.subtotal - totals.finalTotal) > 0.005;
}

export function readDtoolsDisplaySettings(parsedMetadataJson?: unknown): DtoolsDisplaySettings {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return {};
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsDisplaySettings;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const settings = raw as Record<string, unknown>;
  const hiddenOptionKeys = normalizeStoredDtoolsVisibilityKeys(
    Array.isArray(settings.hiddenOptionKeys)
      ? settings.hiddenOptionKeys.map((value) => String(value ?? "").trim()).filter(Boolean)
      : undefined,
  );
  const hiddenLocationKeys = normalizeStoredDtoolsVisibilityKeys(
    Array.isArray(settings.hiddenLocationKeys)
      ? settings.hiddenLocationKeys.map((value) => String(value ?? "").trim()).filter(Boolean)
      : undefined,
  );
  const customerExposeInternalOptionKeys = normalizeStoredDtoolsVisibilityKeys(
    Array.isArray(settings.customerExposeInternalOptionKeys)
      ? settings.customerExposeInternalOptionKeys
          .map((value) => String(value ?? "").trim())
          .filter(Boolean)
      : undefined,
  );
  return {
    hideItemPricing: settings.hideItemPricing === true,
    alternativeOptions: settings.alternativeOptions === true,
    ...(Object.prototype.hasOwnProperty.call(settings, "showPaymentTerms")
      ? { showPaymentTerms: settings.showPaymentTerms === true }
      : {}),
    ...(hiddenOptionKeys?.length ? { hiddenOptionKeys } : {}),
    ...(hiddenLocationKeys?.length ? { hiddenLocationKeys } : {}),
    ...(customerExposeInternalOptionKeys?.length ? { customerExposeInternalOptionKeys } : {}),
    ...(settings.hideClarityIntroduction === true ? { hideClarityIntroduction: true } : {}),
    ...(settings.allowIndividualSystemApproval === true ? { allowIndividualSystemApproval: true } : {}),
    ...(Object.prototype.hasOwnProperty.call(settings, "includeExecutiveOverview")
      ? { includeExecutiveOverview: settings.includeExecutiveOverview === true }
      : {}),
  };
}

export function mergeDtoolsDisplaySettings(
  parsedMetadataJson: unknown,
  patch: Partial<DtoolsDisplaySettings>,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  const prev = readDtoolsDisplaySettings(base);
  base.dtoolsDisplaySettings = {
    ...prev,
    ...patch,
  };
  return base;
}

export function parseDtoolsPaymentTermRow(row: unknown): DtoolsPortalPaymentTerm | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const record = row as Record<string, unknown>;
  const stage = String(record.stage ?? "").trim();
  const percentage = Number(record.percentage ?? 0);
  const fixedAmountRaw = record.fixedAmount;
  const fixedAmount =
    fixedAmountRaw == null || fixedAmountRaw === "" ? null : Number(fixedAmountRaw);
  if (!stage) return null;
  if (fixedAmount != null && Number.isFinite(fixedAmount) && fixedAmount > 0) {
    return { stage, percentage: 0, fixedAmount };
  }
  if (!Number.isFinite(percentage) || percentage <= 0) return null;
  return { stage, percentage };
}

export function readDtoolsPaymentTermsFromJson(paymentTermsJson: unknown): DtoolsPortalPaymentTerm[] {
  if (!Array.isArray(paymentTermsJson)) return [];
  return paymentTermsJson.map(parseDtoolsPaymentTermRow).filter((row): row is DtoolsPortalPaymentTerm => row != null);
}

export function resolveDtoolsReviewPaymentTerms(
  paymentTerms: DtoolsPortalPaymentTerm[] | undefined | null,
  displaySettings?: DtoolsDisplaySettings,
  paymentTermsJson?: unknown,
): DtoolsPortalPaymentTerm[] {
  const rows =
    (paymentTerms?.length ? paymentTerms : null) ??
    readDtoolsPaymentTermsFromJson(paymentTermsJson);

  if (displaySettings?.showPaymentTerms === true) {
    if (rows.length > 0) return rows;
    return [...DTOOLS_DEFAULT_PAYMENT_TERMS];
  }
  if (displaySettings?.showPaymentTerms === false) {
    return [];
  }
  return resolveDtoolsPaymentTerms(paymentTermsJson ?? paymentTerms);
}

export function resolveDtoolsPaymentTerms(paymentTermsJson: unknown): DtoolsPortalPaymentTerm[] {
  const rows = readDtoolsPaymentTermsFromJson(paymentTermsJson);
  if (rows.length > 0) return rows;
  return [...DTOOLS_DEFAULT_PAYMENT_TERMS];
}

export function paymentTermsUseFixedAmounts(terms: DtoolsPortalPaymentTerm[]): boolean {
  return terms.some((term) => term.fixedAmount != null && term.fixedAmount > 0);
}

export function sumPaymentTermPercentages(terms: DtoolsPortalPaymentTerm[]): number {
  return terms.reduce((sum, term) => {
    if (term.fixedAmount != null && term.fixedAmount > 0) return sum;
    return sum + (Number(term.percentage) || 0);
  }, 0);
}

export function validateDtoolsPaymentTerms(terms: DtoolsPortalPaymentTerm[]): {
  valid: boolean;
  totalPercentage: number;
  message?: string;
} {
  const active = terms.filter(
    (term) => (term.fixedAmount != null && term.fixedAmount > 0) || (Number(term.percentage) || 0) > 0,
  );
  if (active.length === 0) {
    return { valid: false, totalPercentage: 0, message: "Add at least one payment term." };
  }
  if (paymentTermsUseFixedAmounts(active)) {
    return { valid: true, totalPercentage: 0 };
  }
  const totalPercentage = sumPaymentTermPercentages(active);
  if (Math.abs(totalPercentage - 100) > 0.01) {
    return {
      valid: false,
      totalPercentage,
      message: `Payment terms must total 100% (currently ${totalPercentage.toFixed(2)}%).`,
    };
  }
  return { valid: true, totalPercentage };
}

/** Payment terms are required only when staff explicitly show them on the customer proposal. */
export function shouldRequireDtoolsPaymentTerms(displaySettings?: DtoolsDisplaySettings): boolean {
  return displaySettings?.showPaymentTerms === true;
}

export function validateDtoolsPaymentTermsWhenVisible(
  terms: DtoolsPortalPaymentTerm[],
  displaySettings?: DtoolsDisplaySettings,
): ReturnType<typeof validateDtoolsPaymentTerms> {
  if (!shouldRequireDtoolsPaymentTerms(displaySettings)) {
    return { valid: true, totalPercentage: 0 };
  }
  return validateDtoolsPaymentTerms(terms);
}

export function formatDtoolsPaymentTermLabel(term: DtoolsPortalPaymentTerm, formatMoney: (n: number) => string): string {
  if (term.fixedAmount != null && term.fixedAmount > 0) {
    return `${formatMoney(term.fixedAmount)} ${term.stage}`;
  }
  const pct = Number(term.percentage) || 0;
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}% ${term.stage}`;
}

export function buildDtoolsProposalSummaryRows(input: {
  systems: DtoolsReviewSystem[];
  footerAdjustments: DtoolsReviewFooter["adjustments"];
  reconciliation?: Parameters<typeof buildDtoolsReviewProposalTotals>[0]["reconciliation"];
  projectTotal?: number | null;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  formatMoney: (amount: number) => string;
  /** Customer proposals omit the alternative-options explanatory notice. */
  audience?: "customer" | "staff";
}): { rows: DtoolsProposalSummaryRow[]; totals: DtoolsReviewProposalTotals } {
  const displaySettings = input.displaySettings ?? {};
  const alternativeOptions = displaySettings.alternativeOptions === true;
  const showAltNotice = alternativeOptions && input.audience === "staff";
  const audience = input.audience ?? "customer";
  const visibleSystems = filterVisibleDtoolsSystems(input.systems, displaySettings, { audience }).map(
    (entry) => entry.system,
  );
  // Visible systems only — never prefer raw header/reconciliation totals that include hidden systems.
  const totals = buildDtoolsReviewProposalTotals({
    systems: visibleSystems,
    footerAdjustments: input.footerAdjustments,
    reconciliation: {
      currency: input.reconciliation?.currency,
      headerTax: input.reconciliation?.headerTax ?? null,
    },
    projectTotal: null,
  });
  const systemViews = buildDtoolsReviewSystemViews(visibleSystems, input.reviewContext);
  const rows: DtoolsProposalSummaryRow[] = [];

  if (alternativeOptions) {
    rows.push({
      key: "alt-intro",
      label: "Proposal options",
      amount: "",
      strong: true,
      ...(showAltNotice ? { helperText: DTOOLS_ALTERNATIVE_OPTIONS_NOTICE } : {}),
    });
    for (const view of systemViews) {
      const label = resolveSystemDisplayLabel(view.system, input.reviewContext);
      rows.push({
        key: `opt-${view.system.systemId ?? view.system.name}`,
        label,
        amount: "",
        strong: true,
        dividerBefore: rows.length > 1,
      });
      for (const room of view.overview.roomTotals) {
        rows.push({
          key: `opt-room-${view.system.name}-${room.name}`,
          label: room.name,
          amount: input.formatMoney(room.total),
          indent: true,
          muted: true,
        });
      }
      if (view.overview.servicesTotal > 0) {
        rows.push({
          key: `opt-services-${view.system.name}`,
          label: "Services",
          amount: input.formatMoney(view.overview.servicesTotal),
          indent: true,
          muted: true,
        });
      }
      rows.push({
        key: `opt-total-${view.system.name}`,
        label: `${label} Total`,
        amount: input.formatMoney(view.overview.systemTotal),
        strong: true,
        dividerBefore: true,
      });
    }
    if (totals.adjustmentsTotal !== 0) {
      rows.push({
        key: "adjustments",
        label: "Adjustments (per agreed option)",
        amount: input.formatMoney(totals.adjustmentsTotal),
        dividerBefore: true,
      });
    }
    return { rows, totals };
  }

  for (const view of systemViews) {
    const label = resolveSystemDisplayLabel(view.system, input.reviewContext);
    rows.push({
      key: `sys-${view.system.systemId ?? view.system.name}`,
      label,
      amount: input.formatMoney(view.overview.systemTotal),
      strong: true,
    });
    for (const room of view.overview.roomTotals) {
      rows.push({
        key: `room-${view.system.name}-${room.name}`,
        label: room.name,
        amount: input.formatMoney(room.total),
        indent: true,
        muted: true,
      });
    }
  }
  if (shouldShowProposalSubtotalRow(totals)) {
    rows.push({
      key: "subtotal",
      label: "Systems total",
      amount: input.formatMoney(totals.subtotal),
      dividerBefore: true,
    });
  }
  if (totals.adjustmentsTotal !== 0) {
    rows.push({
      key: "adjustments",
      label: "Adjustments",
      amount: input.formatMoney(totals.adjustmentsTotal),
    });
  }
  if (totals.tax != null && totals.tax > 0) {
    rows.push({
      key: "tax",
      label: "GST / tax",
      amount: input.formatMoney(totals.tax),
    });
  }
  rows.push({
    key: "final",
    label: "Proposal total",
    amount: input.formatMoney(totals.finalTotal),
    strong: true,
    dividerBefore: true,
  });

  return { rows, totals };
}

export function summaryRowsIncludeCombinedGrandTotal(rows: DtoolsProposalSummaryRow[]): boolean {
  return rows.some((row) => row.key === "final" || row.label.toLowerCase() === "proposal total");
}
